/**
 * Detecta onde o app está rodando: navegador, app desktop (Electron) ou app
 * mobile (Capacitor). Apps nativos usam token Bearer; o navegador usa cookie httpOnly.
 */

interface DesktopBridge {
  isDesktop: true;
  version: string;
  printHtml?: (html: string, opts?: { printer?: string; silent?: boolean; widthMm?: number }) => Promise<void>;
  listPrinters?: () => Promise<{ name: string; isDefault: boolean }[]>;
}

declare global {
  interface Window {
    dezzaDesktop?: DesktopBridge;
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string; Plugins?: Record<string, unknown> };
  }
}

export const isDesktopApp = typeof window !== 'undefined' && Boolean(window.dezzaDesktop?.isDesktop);
export const isNativeMobile = typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());
export const clientType: 'web' | 'mobile' | 'desktop' = isNativeMobile ? 'mobile' : isDesktopApp ? 'desktop' : 'web';
export const usesBearer = clientType === 'mobile';

/** Base da API: mesma origem no navegador (/api via proxy), URL absoluta no app mobile. */
export const API_ORIGIN: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export const APP_NAME: string = (import.meta.env.VITE_APP_NAME as string | undefined) ?? 'Dezza';
export const APP_URL: string = (import.meta.env.VITE_APP_URL as string | undefined) ?? window.location.origin;

export const desktop = typeof window !== 'undefined' ? window.dezzaDesktop : undefined;

const TOKEN_KEY = 'dz.token';

/**
 * Token do app mobile no Keychain (iOS) / Keystore (Android) pelo plugin
 * @aparajita/capacitor-secure-storage (instalado em apps/mobile). Usamos a ponte nativa
 * diretamente para não acoplar o app web ao Capacitor.
 */
interface SecureStorageNative {
  internalGetItem(o: { prefixedKey: string; sync: boolean }): Promise<{ data: string | null }>;
  internalSetItem(o: { prefixedKey: string; data: string; sync: boolean; access: number }): Promise<unknown>;
  internalRemoveItem(o: { prefixedKey: string; sync: boolean }): Promise<unknown>;
}
const SECURE_KEY = `capacitor-storage_${TOKEN_KEY}`;
/** KeychainAccess.afterFirstUnlockThisDeviceOnly: não vai para backups nem para outro aparelho. */
const KEYCHAIN_THIS_DEVICE_ONLY = 3;

function secureStorage(): SecureStorageNative | null {
  const plugin = window.Capacitor?.Plugins?.SecureStorage as SecureStorageNative | undefined;
  return isNativeMobile && plugin && typeof plugin.internalGetItem === 'function' ? plugin : null;
}

function localSet(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* armazenamento indisponível */
  }
}

let memoryToken: string | null = null;

/** Carrega o token antes de o app montar (no celular, do armazenamento seguro; migra o antigo). */
export async function initTokenStore(): Promise<void> {
  if (!usesBearer) return;
  let legacy: string | null = null;
  try {
    legacy = window.localStorage.getItem(TOKEN_KEY);
  } catch {
    /* sem localStorage */
  }
  const secure = secureStorage();
  if (!secure) {
    memoryToken = legacy;
    return;
  }
  try {
    const { data } = await secure.internalGetItem({ prefixedKey: SECURE_KEY, sync: false });
    memoryToken = data ?? legacy;
    if (legacy) {
      if (!data) await secure.internalSetItem({ prefixedKey: SECURE_KEY, data: legacy, sync: false, access: KEYCHAIN_THIS_DEVICE_ONLY });
      localSet(null);
    }
  } catch {
    memoryToken = legacy;
  }
}

export const tokenStore = {
  get(): string | null {
    return usesBearer ? memoryToken : null;
  },
  set(token: string | null) {
    if (!usesBearer) return;
    memoryToken = token;
    const secure = secureStorage();
    if (!secure) return localSet(token);
    const op = token
      ? secure.internalSetItem({ prefixedKey: SECURE_KEY, data: token, sync: false, access: KEYCHAIN_THIS_DEVICE_ONLY })
      : secure.internalRemoveItem({ prefixedKey: SECURE_KEY, sync: false });
    Promise.resolve(op).catch(() => localSet(token));
  },
};

/**
 * App mobile: liga a integração automática com o sistema (cor dos ícones das barras acompanha o tema).
 * Import dinâmico: no navegador e no desktop o arquivo nem é carregado. Leitor de código de barras,
 * vibração, compartilhar e rede para as telas: veja lib/native.ts.
 */
if (isNativeMobile) {
  void import('./native').then((m) => m.startNativeShell()).catch(() => undefined);
}
